import React, { useCallback, useMemo, useRef, useState } from 'react';
import { get, find, filter } from 'lodash';
import { debounce } from 'lodash';

import {
	classNames,
	useClassNames,
} from '@common';

import {
	IconButton, Textbox,
} from '@components';

import {
	SelectArrowIcon,
	ChipRemoveIcon
} from '@icons';


import './Chips.scss';

interface ChipsSelectProps<T>{
	onChange?: (values: T[]) => void,
	values: T[],
	suggestedValues: T[],
    processQuery?: (query: string, maxResults: number) => Promise<T[]>,
    queryDisabled?: boolean,
	name?: string,
	className?: string,
	disabled?: boolean,
    queryHint?: string,
    valueIdAttribute?: string,
    valueNameAttribute?: string,
    maxValues?: number,
}

const ChipsSelect = <T,>(props: ChipsSelectProps<T>): JSX.Element => {
	const {
		className,
		disabled,
		onChange,
		values,
		name,
		suggestedValues,
        processQuery,
        queryHint,
        valueIdAttribute,
        valueNameAttribute,
        queryDisabled,
		maxValues,
	} = props;

	const [showOptions, setShowOptions] = useState<boolean>(false);
	const [query, setQuery] = useState<string>('');
	const [queryOptions, setQueryOptions] = useState<T[]>([]);

    const idAttr = valueIdAttribute || "id";
    const nameAttr = valueNameAttribute || "name";

	const contentRef = useRef<HTMLDivElement>(null);
	const targetRef = useRef<HTMLDivElement>(null);

	const handleChange = useCallback((e, option: T) => {
		e.stopPropagation();

        if (!find(values, v => v[idAttr] == option[idAttr])) {
            onChange([...values, option]);
        }
		setShowOptions(false);
		setQuery('');
		setQueryOptions([]);

	}, [onChange, setShowOptions, values, idAttr]);

    const handleRemove = useCallback((e, option: T) => {
		e.stopPropagation();

        const filtered = filter(values, v => v[idAttr] != option[idAttr]);
        onChange(filtered);
        setShowOptions(false);
		setQuery('');
		setQueryOptions([]);

	}, [onChange, values, setShowOptions, idAttr]);

    const textName = 'name';

	const notAtLimit = useMemo(() => {
		return !maxValues || !values || values.length < maxValues;
	}, [maxValues, values]);

	const optionsView = useMemo((): JSX.Element => {
		const renderOption = (option: T) => {
			const
				isSelected = find(values, v => v[idAttr] == option[idAttr]),
				classes = classNames(
					'chips-select__option',
					isSelected && 'selected',
				);

			return (
				<div
					onClick={e => handleChange(e, option)}
					onMouseDown={e => e.preventDefault()}
					className={classes}
					key={option[idAttr]}
				>
					{textName ? get(option, textName) : option}
				</div>
			);
		};

        const valuesIds = values && values.map(v => v[idAttr]);
        const filteredSuggestions = filter(suggestedValues, v => !valuesIds || !valuesIds.includes(v[idAttr]));

		return showOptions && (suggestedValues || queryOptions) && notAtLimit && (
			<div className="chips-select__options" ref={contentRef}>
				{filteredSuggestions && filteredSuggestions.map(renderOption)}
				<div className="chips-select__option-separator"></div>
				{!query && !queryDisabled && <div className='chips-select__option hint'>
					{queryHint || "Начните ввод для поиска"}
				</div>}
				{query && !queryOptions.length && <div className='chips-select__option hint'>
					Поиск не дал результатов
				</div>}
				{query && queryOptions.map(renderOption)}
			</div>
		);
	}, [values, suggestedValues, query, queryOptions, handleChange, notAtLimit,
        showOptions, textName, queryHint, idAttr, queryDisabled]);

	const trailingView = useMemo((): JSX.Element => {
		return (
			<IconButton
				icon={<SelectArrowIcon />}
				iconSize="20px"
				size="nogap"
			/>
		);
	}, []);

	const classes = useClassNames(
		'chips-select',
		className,
		disabled && 'disabled',
	);

    const chipsView = useMemo(() => {

        return values && values.map(val => <div key={val[idAttr]} className='chip nobr'>
            {val[nameAttr]}
            <div
                onClick={(e) => handleRemove(e, val)}
                className='chip__remove'
            >
                <IconButton
                    icon={<ChipRemoveIcon />}
                    iconSize="10px"
                    size="nogap"
                />
            </div>
        </div>
        );
    }, [values, handleRemove, idAttr, nameAttr]);

	const debouncedQuery = useRef(
		debounce(async (newQuery: string, maxItems: number) => {
			const resutls = await processQuery(newQuery, maxItems);
			setQueryOptions(resutls);
			setShowOptions(true);
		}, 300)
	).current;

	const handleQueryChange = useCallback((newQuery: string) => {
		setQuery(newQuery);
		if (!newQuery) {
			setQueryOptions([]);
			setShowOptions(true);
			return;
		}

		debouncedQuery(newQuery, 50);		
	}, [debouncedQuery]);

	

	return (
		<div className={classes} id={name}>
			<div className="chips-select__leading">
			</div>
			<div className="chips-select__value">
                <div className='chips'>
                    {chipsView}
                    {!queryDisabled && notAtLimit && <Textbox
						className="chips-select__query"
						value={query}
						onValueChange={handleQueryChange} 
						onBlur={() => setShowOptions(false)}
						onFocus={() => setShowOptions(true)}
					/>}
                </div>
				{optionsView}
			</div>
			{notAtLimit &&
				<div
					className="chips-select__trailing"
					ref={targetRef}
					onClick={() => setShowOptions(!showOptions)}
				>
					{trailingView}
				</div>
			}
		</div>
	);
}

const MemoisedChipsSelect = React.memo(ChipsSelect);

export {MemoisedChipsSelect as ChipsSelect};